import React, { ReactNode } from "react";
import { Controller } from "react-hook-form";
import {
    Checkbox,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    DatePicker,
    LocalizationProvider,
} from "@comfama/comfama-ui";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import { Typography } from "@mui/material";
import rawToReact from "../../../utils/rawToReact";
import contentfulRenderOptions from "../../../utils/contentfulRenderOption";
import "./FormInput.scss"
import "../../molecules/form-bottom/FormBottom.scss"
// Interface Global con todos las pros de contentful
export interface GlobalInputProps {
    type: string;
    name: string;
    label: string;
    options?: {label: string | number, value: string | number}[];
    helperText:string | ReactNode;
    section: string;
    tabOrder?: string | number;
    placeholder: string;
    msnAdvice: string;
    required: boolean;
    disabled: boolean;
    fieldMandatory?: string;
    maxLength: number;
    textoTerminos?: {raw: string} | null;
}

// Interface de las props del componente
interface FormInputProps {
    attributes: GlobalInputProps;
    control: any;
    tagUseForm?: string;
    alertMessage?: boolean;
    // onChange: (e) => void;
}


// Componente que renderiza los inputs dependiendo del tipo de campo
export const FormInputLib = ({
    attributes,
    control,
    tagUseForm,
    alertMessage,
    // onChange,
}: FormInputProps) => {
    switch (attributes.type) {
        case "email":
        case "text":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                        </InputLabel>
                    </div>
                    <Controller
                        name={attributes.name + tagUseForm ?? ""}
                        control={control}
                        rules={{ required: attributes.required }}
                        render={({ field, formState: {errors} }) => (
                            <>
                                <TextField
                                    {...field}
                                    type={attributes.type}
                                    id={attributes.name + tagUseForm ?? ""}
                                    placeholder={attributes.placeholder}
                                    fullWidth
                                    inputProps={{maxLength:attributes.maxLength}}
                                    helperText = {attributes.helperText}
                                />
                                {errors[attributes.name] && 
                                    <Typography variant="subtitle2" component="span" color="error.500">
                                        {errors[attributes.name]!.message as string}
                                    </Typography>
                                }
                            </>
                        )}
                    />
                </>
            );
        case "select":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                        </InputLabel>
                    </div>
                    <div className="select-container">
                        <Controller
                            name={attributes.name + tagUseForm ?? ""}
                            control={control}
                            rules={{ required: attributes.required }}
                            render={({ field }) => (
                            <Select
                                {...field}
                                displayEmpty
                                placeholder={attributes.placeholder}
                                fullWidth
                            >
                                <MenuItem value="" disabled>
                                    {attributes.placeholder}
                                </MenuItem>
                                    {attributes.options?.map((item) => (
                                <MenuItem key={item.label} value={item.value}>
                                    {item.label}
                                </MenuItem>
                                ))}
                            </Select>
                            )}
                        />
                    </div>
                </>
            );
        case "text-area":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                        </InputLabel>
                    </div>
                    <Controller
                        name={attributes.name + tagUseForm ?? ""}
                        control={control}
                        rules={{ required: attributes.required, maxLength:{value:attributes.maxLength, message:""}}}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            multiline
                            rows={4}
                            id={attributes.name + tagUseForm ?? ""}
                            placeholder={attributes.placeholder}
                            fullWidth
                            inputProps={{maxLength:attributes.maxLength}}
                            onChange={(e) => {
                                if (e.target.value.length <= attributes.maxLength) {
                                  field.onChange(e);
                                }
                              }}
                        />
                        )}
                    />
                </>
            );
        case "mobile":
        case "number":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                        </InputLabel>
                    </div>
                    <Controller
                        name={attributes.name + tagUseForm ?? ""}
                        control={control}
                        rules={{ required: attributes.required, maxLength:{value:attributes.maxLength, message:""}}}
                        render={({ field, formState: {errors} }) => (
                        <>
                            <TextField
                                {...field}
                                type="number"
                                id={attributes.name + tagUseForm ?? ""}
                                placeholder={attributes.placeholder}
                                fullWidth
                                inputProps={{
                                    maxLength:attributes.maxLength, 
                                    onWheel: (e) => e.currentTarget.blur()
                                }}
                                onChange={(e) => {
                                    if (e.target.value.length <= attributes.maxLength) {
                                        field.onChange(e);
                                    }
                                }}
                                helperText = {attributes.helperText}
                            />
                            {errors[attributes.name] && 
                                <Typography variant="subtitle2" component="span" color="error.500">
                                    {errors[attributes.name]!.message as string}
                                </Typography>
                            }
                        </>
                        )}
                    />
                </>
            );
        case "date":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                        </InputLabel>
                    </div>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <Controller
                            name={attributes.name + tagUseForm ?? ""}
                            control={control}
                            rules={{ required: attributes.required }}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width:"100%"}}
                                    {...field}
                                    views={['year', 'month', 'day']}
                                    format="DD-MM-YYYY"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(date) => field.onChange(date)}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </>
              );
        case "radio":
            return (
                <>
                    <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                        <InputLabel htmlFor={attributes.name + tagUseForm ?? ""}>
                            {attributes.label}
                            {attributes.textoTerminos && 
                                <div className="aux-data">
                                    {rawToReact(
                                        attributes.textoTerminos.raw,
                                        contentfulRenderOptions
                                    )}
                                </div>
                            }
                        </InputLabel>
                    </div>
                    <Controller
                        name={attributes.name + tagUseForm ?? ""}
                        control={control}
                        rules={{ required: attributes.required }}
                        render={({ field }) => (
                        <RadioGroup {...field}>
                            {attributes.options?.map((item) => (
                            <FormControlLabel
                                key={item.label}
                                value={item.value}
                                control={<Radio />}
                                label={item.label}
                            />
                            ))}
                        </RadioGroup>
                        )}
                    />
                    {alertMessage && attributes.name === "dataAuthorization" && (
                        <div className="a-input__form__toast-advertiser">
                            <div className="toast-advertiser__img">
                                <img
                                    src="https://images.ctfassets.net/203frbsx1z3n/5nj7ecMSriXgVqpvZKULO/b693785ad612b64d7437ab2ad492d8ea/icon-alert.svg"
                                    alt="icono-error"
                                    style={{ maxWidth: 24, maxHeight: 24 }}
                                />
                            </div>
                            <div className="toast-advertiser__description">
                                <p>Para continuar debes aceptar el tratamiento de datos.</p>
                            </div>
                        </div>
                    )}
                </>
            );
        case "checkbox":
            return (
                <div className={`${attributes.required && "form-input-required" } form-input-label`}>
                    {attributes.textoTerminos && 
                        <div className="authorization-data">
                            {rawToReact(
                                attributes.textoTerminos.raw,
                                contentfulRenderOptions
                            )}
                        </div>
                    }
                    <Controller
                        name={attributes.name + tagUseForm ?? ""}
                        control={control}
                        rules={{ required: attributes.required }}
                        render={({ field }) => (
                            <FormControlLabel
                            control={
                                <Checkbox
                                {...field}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                />
                            }
                            label={attributes.label}
                            />
                        )}
                    />
                </div>
            );
    }
    return null;
};
